import React from "react";
import PropTypes from "prop-types";
import { connect, useSelector } from "react-redux";
import { get } from "utils/api";
import { errorMessage } from "actions/message";
import { useFetch } from "hooks";
import { Waiting, ListItem } from "components/ui";
import AudioPlayer from "components/ui/audio-player";
import { formatPhone } from "utils/misc";
import moment from "moment";
import { path } from "ramda";
import { Link, withRouter } from "react-router-dom";
import { browserHistory } from "browser-history";
import { selectors } from "reducers";
import formatTranscript from "features/synthpop/format-transcript";

// Format outcome for display (e.g., ORDERED_SUPPLIES -> Ordered Supplies)
const formatOutcome = outcome => {
  if (!outcome) return "Unknown";
  return outcome
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Format duration seconds to mm:ss
const formatDuration = seconds => {
  if (!seconds) return "N/A";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Synthpop Agent Call Profile Component
const SynthpopCallProfile = ({ call, isFetching }) => {
  const [showTranscript, setShowTranscript] = React.useState(false);

  return (
  <div
    className={`twilio-call-profile-content ${isFetching ? "is-fetching" : ""}`}
  >
    <div className="call-summary-container">
      <div className="call-direction-container">
        <div className="call-direction-header">
          <div className="direction-label">From</div>
          <div className="direction-info">
            <span>{formatPhone(call.fromNumber)}</span>
            <span>{call.companyName || "Agent System"}</span>
          </div>
        </div>
        <div className="call-direction-header">
          <div className="direction-label">To</div>
          <div className="direction-info">
            <span>{formatPhone(call.toNumber)}</span>
            <span>{call.patientName}</span>
          </div>
        </div>
        <div className="call-type-container">
          <span className="summary-label">Type:</span>
          <span>Agent Call</span>
          <span className="summary-label">Requested By:</span>
          <span>{call.requestorName}</span>
        </div>
        <div className="call-direction-details">
          <span className="summary-label">Outcome:</span>
          <span>{formatOutcome(call.outcome)}</span>
          <span className="summary-label">Status:</span>
          <span>{call.status}</span>
        </div>
      </div>
    </div>

    <div className="call-duration-container">
      <ListItem
        label="Call Duration"
        value={formatDuration(call.callDurationSeconds)}
        underline
      />
      <ListItem
        label="Transferred to S3"
        value={call.transferredToHuman ? "Yes" : "No"}
        underline
      />
      <ListItem
        label="Secondary Attempt"
        value={call.isSecondaryAttempt ? "Yes" : "No"}
        underline
      />
      <ListItem
        label="Order Created"
        value={call.orderCreated ? "Yes" : "No"}
        underline
      />
      {call.needsAttention && (
        <ListItem
          label="Needs Attention"
          value="Yes"
          underline
        />
      )}
    </div>

    {call.callSummary && (
      <div className="call-summary-text-container">
        <h4>Call Summary</h4>
        <p className="call-summary-text">{call.callSummary}</p>
      </div>
    )}

    {call.transcript && (
      <div className="transcript-container">
        <div className="transcript-header">
          <h4>Transcript</h4>
          <button
            className="transcript-toggle"
            onClick={() => setShowTranscript(!showTranscript)}
          >
            {showTranscript ? "Hide Transcript" : "Show Transcript"}
          </button>
        </div>
        {showTranscript && (
          <div className="transcript-text">{formatTranscript(call.transcript)}</div>
        )}
      </div>
    )}

    <div className="recording-container">
      <h4>Recording</h4>
      {call.recordingUrl ? (
        <AudioPlayer src={call.recordingUrl} />
      ) : (
        <div>No Recording</div>
      )}
    </div>

    <div className="call-time-container">
      <ListItem
        label="Task Created"
        value={
          call.taskCreated &&
          moment(call.taskCreated).format("MMM DD, YYYY -  hh:mm A")
        }
        underline
      />
      <ListItem
        label="Callback Received"
        value={
          call.callbackReceived
            ? moment(call.callbackReceived).format("MMM DD, YYYY -  hh:mm A")
            : "Pending"
        }
      />
    </div>

    {call.errorStage && (
      <div className="error-container">
        <h4>Error Information</h4>
        <ListItem label="Error Stage" value={call.errorStage} underline />
        <ListItem label="Error Message" value={call.errorMessage} />
      </div>
    )}
  </div>
  );
};

SynthpopCallProfile.propTypes = {
  call: PropTypes.object.isRequired,
  isFetching: PropTypes.bool
};

// Twilio Call Profile Component (existing logic)
const TwilioCallProfile = ({ call, isFetching }) => (
  <div
    className={`twilio-call-profile-content ${isFetching ? "is-fetching" : ""}`}
  >
    <div className="call-summary-container">
      <div className="call-direction-container">
        <div className="call-direction-header">
          <div className="direction-label">From</div>
          <div className="direction-info">
            <span>{formatPhone(call.fromNumber)}</span>
            <span>{fromLabel(call)}</span>
          </div>
        </div>
        <div className="call-direction-header">
          <div className="direction-label">To</div>
          <div className="direction-info">
            <span>{formatPhone(call.toNumber)}</span>
            <span>{toLabel(call)}</span>
          </div>
        </div>
        <div className="call-type-container">
          <span className="summary-label">Type:</span>
          <span>{call.type}</span>
          <span className="summary-label">Client:</span>
          <span>{clientLabel(call)}</span>
        </div>
        <div className="call-direction-details">
          <span className="summary-label">Direction:</span>
          <span>{call.direction}</span>
          <span className="summary-label">Status:</span>
          <span>{call.status}</span>
        </div>
      </div>
    </div>
    <div className="call-duration-container">
      <ListItem
        label="Hold/Ring Time"
        value={call.holdRingTime.substring(3)}
        underline
      />
      <ListItem
        label="Talk Time"
        value={call.TalkTime.substring(3)}
        underline
      />
      <ListItem
        label="Voicemail Duration"
        value={call.VMTime.substring(3)}
        underline
      />
      <ListItem
        label="Transfer Time"
        value={call.transferTime.substring(3)}
        underline
      />
      <ListItem
        label="Time In S3"
        value={call.S3Time.substring(3)}
        underline
      />
      <ListItem label="Total Time" value={call.totalTime.substring(3)} />
    </div>
    <div className="recording-container">
      <h4>Recording</h4>
      {call.recordingURL ? (
        <AudioPlayer src={call.recordingURL} />
      ) : (
        <div>No Recording</div>
      )}
    </div>
    <div className="call-time-container">
      <ListItem
        label="Start"
        value={
          call.start && moment(call.start).format("MMM DD, YYYY -  hh:mm A")
        }
        underline
      />
      <ListItem
        label="Answered"
        value={
          call.answered &&
          moment(call.answered).format("MMM DD, YYYY -  hh:mm A")
        }
        underline
      />
      <ListItem
        label="End"
        value={
          call.end && moment(call.end).format("MMM DD, YYYY -  hh:mm A")
        }
      />
    </div>
  </div>
);

TwilioCallProfile.propTypes = {
  call: PropTypes.object.isRequired,
  isFetching: PropTypes.bool
};

const ContactRecordProfile = ({ callSid, synthpopUUID, isModal }) => {
  const isSynthpopCall = !!synthpopUUID;
  const userRole = useSelector(selectors.getUserRole);
  const isTrueAdmin = userRole === "Administrator";

  // Fetch hook for Twilio calls
  const twilioFetch = useFetch({
    apiFn: sid => get(`twilio/callDetails/${sid}`),
    transformError: path(["response", "body", "status"]),
  });

  // Fetch hook for Synthpop calls
  const synthpopFetch = useFetch({
    apiFn: uuid => get(`synthpop/call-profile/${uuid}`),
    transformError: path(["response", "body", "status"]),
  });

  const { response: call, isFetching, error } = isSynthpopCall
    ? synthpopFetch
    : twilioFetch;

  React.useEffect(() => {
    if (isSynthpopCall) {
      synthpopFetch.fetchData(synthpopUUID);
    } else if (callSid) {
      twilioFetch.fetchData(callSid);
    }
  }, [callSid, synthpopUUID]);

  const displayId = isSynthpopCall ? synthpopUUID : callSid;
  const headerTitle = isSynthpopCall ? "Agent Call Log" : "Call Log";
  const taskUrl = call?.synthpopTaskURL;

  return (
    <div className="twilio-call-profile">
      <div className="page-header">
        <h2>
          {headerTitle}: <span>{displayId}</span>
        </h2>
        {taskUrl && isTrueAdmin && (
          <a
            href={taskUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="synthpop-task-link"
          >
            Synthpop Task
          </a>
        )}
        {!isModal && (
          <button
            style={{ margin: 0, padding: 10 }}
            onClick={browserHistory.goBack}
          >
            Go Back
          </button>
        )}
      </div>
      {error && !isFetching && <div className="fetch-error">{error}</div>}
      {isFetching && <Waiting size="50px" />}
      {call && (
        isSynthpopCall ? (
          <SynthpopCallProfile call={call} isFetching={isFetching} />
        ) : (
          <TwilioCallProfile call={call} isFetching={isFetching} />
        )
      )}
    </div>
  );
};

ContactRecordProfile.propTypes = {
  callSid: PropTypes.string,
  synthpopUUID: PropTypes.string,
  errorMessage: PropTypes.func.isRequired,
  isModal: PropTypes.bool
};

export default connect(() => ({}), { errorMessage })(ContactRecordProfile);

const fromLabel = ({ direction, companyID, fromName }) =>
  direction === "outgoing" && companyID ? (
    <Link to={`/companies/${companyID}/profile`}>{fromName}</Link>
  ) : (
    fromName
  );
fromLabel.propTypes = {
  direction: PropTypes.string,
  companyID: PropTypes.string,
  fromName: PropTypes.string
};

const toLabel = ({ direction, companyID, toName }) =>
  direction === "incoming" && companyID ? (
    <Link to={`/companies/${companyID}/profile`}>{toName}</Link>
  ) : (
    toName
  );
toLabel.propTypes = {
  direction: PropTypes.string,
  companyID: PropTypes.string,
  toName: PropTypes.string
};

const clientLabel = ({ clientName, client }) =>
  client ? (
    <Link to={`/users/${client.replace("client:", "")}`}>{clientName}</Link>
  ) : (
    clientName
  );
clientLabel.propTypes = {
  clientName: PropTypes.string,
  client: PropTypes.string
};

export const ContactRecordProfileRoute = withRouter(({ match }) => (
  <ContactRecordProfile callSid={match.params.sid} />
));
